import React, { useCallback, useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import _ from 'lodash'

import LineChart from '../../linechart'
import PaceLoader from '../../paceloader'
import Heading from '../heading'
import RefreshButton from '../refresh_button'
import AnalyticsFilters from '../analytics_filters'
import ReportTableOrg from './report_org_table'
import ReportTableIndividual from './report_individual_table'

import { ApiHelper } from '../../../../helpers/apihelper'
import * as Constants from '../../../../helpers/constants'
import { buildApiFilters, getMetrics, getTableHeading, isOrgAdminView } from '../helpers'

/**
 * Port of cowebapp analytics/components/data_pulse/index.js — Data Pulse
 * Usage Reports (report/data_pulse endpoint; stats keyed email/phone/name
 * with billable).
 */
const DataPulse = ({ client, viewedUser, memberOptions }) => {

  const [metrics, setMetrics] = useState({
    total: 0, email: 0, phone: 0, name: 0
  })
  const [isPerfSummaryOpen, setIsPerfSummaryOpen] = useState(true)
  const [usageStats, setUsageStats] = useState([])
  const [usageReports, setUsageReports] = useState([])
  const [showTable, setShowTable] = useState('individual')
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [graphData, setGraphData] = useState(null)
  const [isGraphDataLoading, setIsGraphDataLoading] = useState(false)
  const [filters, setFiltersState] = useState({})

  const summaryToggle = () => {
    setIsPerfSummaryOpen(!isPerfSummaryOpen)
  }

  // labels shown directly (webapp maps 'email'/'phone'/'name' through
  // SERVICE_BASED_REPORT_FIELDS.form_guard → Email/Phone/Name Contacts)
  const getGraphData = useCallback((data) => {
    const stat = data.user_stats?.[0]
    if (!stat || !stat.stats?.length) return null

    let graphData = {
      breakdown: data.breakdown,
      labels: [],
      values: []
    }

    const types = [
      { key: 'email', label: 'Email Contacts' },
      { key: 'phone', label: 'Phone Contacts' },
      { key: 'name', label: 'Name Contacts' }
    ]
    const colorMap = {
      email: '#ffa500', // orange
      phone: '#3cba9f', // green
      name: '#c45850'   // red
    }

    stat.stats.forEach(entry => {
      graphData.labels.push(entry.date)
    })

    graphData.values = types.map(type => {
      const color = colorMap[type.key] || '#888'

      return {
        label: type.label,
        data: stat.stats.map(entry => entry[type.key]?.total || 0),
        borderColor: color,
        backgroundColor: color + '14',
        fill: true
      }
    })

    return graphData
  }, [])

  const getGraphOrgData = useCallback((data) => {
    const arrStats = []

    data.user_stats.forEach(user => {
      user.stats.forEach(st => {
        const date = st.date
        arrStats.push({
          date,
          email: st.email,
          phone: st.phone,
          name: st.name
        })
      })
    })

    const objStats = {}

    arrStats.forEach(d => {
      const date = d.date
      if (!objStats[date]) {
        objStats[date] = JSON.parse(JSON.stringify(d)) // deep copy
      } else {
        // Aggregate email stats
        for (let key in d.email) {
          if (typeof d.email[key] === 'object') {
            for (let subKey in d.email[key]) {
              objStats[date].email[key][subKey] += d.email[key][subKey]
            }
          } else {
            objStats[date].email[key] += d.email[key]
          }
        }

        // Aggregate phone stats
        for (let key in d.phone) {
          objStats[date].phone[key] += d.phone[key]
        }

        // Aggregate name stats
        for (let key in d.name) {
          objStats[date].name[key] += d.name[key]
        }
      }
    })

    const userStatsOrg = [{
      stats: Object.entries(objStats).map(([date, value]) => ({
        date,
        email: value.email,
        phone: value.phone,
        name: value.name
      }))
    }]

    data.user_stats = userStatsOrg

    return getGraphData(data)
  }, [getGraphData])

  const setterMetrics = useCallback((stats, role) => {
    const metrics = {
      total: 0,
      email: 0,
      phone: 0,
      name: 0
    }

    if (role === 'all') {
      // stats = array of users
      stats.forEach(user => {
        user.stats.forEach(stat => {
          const emailTotal = stat.email?.billable || 0
          const phoneTotal = stat.phone?.billable || 0
          const nameTotal = stat.name?.billable || 0

          metrics.email += emailTotal
          metrics.phone += phoneTotal
          metrics.name += nameTotal
          metrics.total += emailTotal + phoneTotal + nameTotal
        })
      })
    } else {
      // stats = flat array of stats
      stats.forEach(stat => {
        const emailTotal = stat.email?.billable || 0
        const phoneTotal = stat.phone?.billable || 0
        const nameTotal = stat.name?.billable || 0

        metrics.email += emailTotal
        metrics.phone += phoneTotal
        metrics.name += nameTotal
        metrics.total += emailTotal + phoneTotal + nameTotal
      })
    }

    return metrics
  }, [])

  const loadReportData = useCallback(() => {
    setIsGraphDataLoading(true)
    setIsStatsLoading(true)

    const { filters: apiFilters, selectedUserState } = buildApiFilters({
      viewedUser,
      memberFilter: filters.members,
      dateRangeFilter: filters.date_range
    })

    const queryParams = _.pick(
      { user_id: viewedUser.id, filters: JSON.stringify(apiFilters) },
      ApiHelper(client, 'DATA_PULSE_REPORTS').API_QUERY
    )

    ApiHelper(client, 'DATA_PULSE_REPORTS').API(client, queryParams)
      .then((res) => {
        setIsGraphDataLoading(false)
        if (res && res.status === Constants.CO_API.SUCCESS) {

          let usageStatsIndividualTable = selectedUserState.value.role === 'all' ?
            res.data.user_stats : res.data.user_stats[0].stats
          setUsageReports(res.data)
          setShowTable(selectedUserState.value.role === 'all' ? 'orgAllData' : 'individual')
          setUsageStats(usageStatsIndividualTable)
          setIsStatsLoading(false)
          let collectGraphData = selectedUserState.value.role === 'all'
            ? getGraphOrgData(res.data) : getGraphData(res.data)
          setGraphData(collectGraphData)
          setMetrics(setterMetrics(usageStatsIndividualTable, selectedUserState.value.role))
        }
      })
      .catch((err) => {
        setIsGraphDataLoading(false)
        setIsStatsLoading(false)
        console.log('UsageReports api erred', err)
      })
  }, [client, viewedUser, filters, getGraphData, getGraphOrgData, setterMetrics])

  useEffect(() => {
    if (!_.isEmpty(filters)) {
      loadReportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const refreshReports = () => {
    loadReportData()
  }

  const onSetFilters = (filters) => {
    setFiltersState({ ...filters })
  }

  return (
    <div className="analytics-graph-content">
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <div className="report-date-filter">
          <Heading
            toolTipMessage="Find your credit utilization of Data Pulse"
            title="Data Pulse Usage Reports"
          />
        </div>
      </div>
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <AnalyticsFilters
          client={client}
          viewedUser={viewedUser}
          memberOptions={memberOptions}
          filterState={filters}
          onSetFilters={onSetFilters}
        />
        <RefreshButton onRefresh={refreshReports} />
      </div>
      <div className="report-content ps-3 pe-3">
        <PaceLoader isLoading={isStatsLoading} />
        <div>
          <div className="d-flex report-performance-summary g-0 flex-column pt-3">
            <div className="page-report-heading">
              <h3 className="m-0">Summary</h3>
              <div className="btn-toolbar">
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => summaryToggle()}
                  >
                    {isPerfSummaryOpen === true ? (
                      <i className="fa fa-chevron-up"></i>
                    ) : (
                      <i className="fa fa-chevron-down"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <Collapse in={isPerfSummaryOpen}>
              <div className="graph-content">
                <div className="metrics">
                  <div className="metrics-content">
                    <div className="total-verified">
                      <span>Total Credits</span>
                      {getMetrics(
                        metrics.total,
                        2,
                        '',
                        '',
                        'Total Credits'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Email Validation</span>
                      {getMetrics(
                        metrics.email,
                        2,
                        '',
                        '',
                        'Email Validation'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Phone Validation</span>
                      {getMetrics(
                        metrics.phone,
                        2,
                        '',
                        '',
                        'Phone Validation'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Name Validation</span>
                      {getMetrics(
                        metrics.name,
                        2,
                        '',
                        '',
                        'Name Validation'
                      )}
                    </div>
                  </div>
                </div>
                <div className="area-graph">
                  <LineChart
                    graphData={graphData}
                    isGraphDataLoading={isGraphDataLoading}
                    width={300}
                    height={250}
                    position="bottom"
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div>
          <div className="d-flex report-performance-breakdown g-0 flex-column">
            <div className="page-report-heading">
              <h3 className="m-0 d-flex align-items-center">
                {getTableHeading(isOrgAdminView(viewedUser) ? 'all' : viewedUser.user_role, usageReports.breakdown)}
              </h3>
            </div>
            {!isStatsLoading &&
              showTable === 'orgAllData'
              ? <ReportTableOrg
                userReportData={usageReports}
                tableData={usageStats}
              />
              : <ReportTableIndividual
                userReportData={usageReports}
                tableData={usageStats}
              />
            }
          </div>
        </div>
        <div className="report-footnote">
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
          >
          </i>
          <small className="form-text">
            Row will be supressed when no activity found for the particular
            day or hour for the chosen period
          </small>
        </div>
      </div>
    </div>
  )
}

export default DataPulse
